/**
 * labDecksAdmin.service.ts — Admin > Integrations > Lab Decks (legacy
 * lib/admin/admin-labdeck-list.php): slide decks the Quest results viewer
 * walks a patient through, through /lab-decks. Writes need Admin Access.
 *
 *   deck › slides (an HTML slide, or a lab slide: left / right content around
 *   its tests) › tests (matched to a result by biomarker or Diagnosis Code,
 *   drawn as a gauge over three ranges).
 */
import { apiClient } from '@/services/apiClient';
import { uploadFile } from '@/services/uploads.service';
import type { ApiResponse } from '@/types/api.types';

export type SlideKind = 'html' | 'lab';

export interface LabSlide {
  id: number;
  deckId: number;
  title: string;
  kind: SlideKind;
  customContent: string;
  leftContent: string;
  rightContent: string;
  active: boolean;
  order: number;
  testCount: number;
}

export interface LabDeck {
  id: number;
  title: string;
  active: boolean;
  order: number;
  slides: LabSlide[];
}

export interface TestRange {
  title: string;
  /** '#rrggbb' or ''. */
  color: string;
  low: string;
  high: string;
}

export interface DeckTest {
  /** 0 for one not saved yet. */
  id: number;
  title: string;
  /** Diagnosis Code (OBX-3.1). */
  labCode: string;
  /** 0 = none. */
  biomarkerId: number;
  description: string;
  /** A bare file name in the account's icons folder ('' = none). */
  iconImage: string;
  ranges: [TestRange, TestRange, TestRange];
  increment: string;
  /** '' every patient, 'F' or 'M'. */
  gender: '' | 'F' | 'M';
  optimalText: string;
}

export interface Biomarker {
  id: number;
  name: string;
  unit: string;
}

export const DECK_TITLE_MAX = 255;

export const GENDER_OPTIONS: { value: '' | 'F' | 'M'; label: string }[] = [
  { value: '', label: 'Every patient' },
  { value: 'F', label: 'Female' },
  { value: 'M', label: 'Male' },
];

export function blankTest(): DeckTest {
  const range = (): TestRange => ({ title: '', color: '', low: '', high: '' });
  return { id: 0, title: '', labCode: '', biomarkerId: 0, description: '', iconImage: '', ranges: [range(), range(), range()], increment: '1', gender: '', optimalText: '' };
}

export function listLabDecks(): Promise<LabDeck[]> {
  return apiClient.get<ApiResponse<LabDeck[]>>('/lab-decks').then((r) => r.data.data ?? []);
}

export function createLabDeck(title: string): Promise<number> {
  return apiClient.post<ApiResponse<{ id: number }>>('/lab-decks', { title: title.trim() }).then((r) => Number(r.data.data?.id) || 0);
}

export function renameLabDeck(id: number, title: string): Promise<void> {
  return apiClient.put(`/lab-decks/${id}`, { title: title.trim() }).then(() => undefined);
}

export function deleteLabDeck(id: number): Promise<void> {
  return apiClient.delete(`/lab-decks/${id}`).then(() => undefined);
}

export function restoreLabDeck(id: number): Promise<void> {
  return apiClient.post(`/lab-decks/${id}/restore`).then(() => undefined);
}

export type SlidePayload = Pick<LabSlide, 'deckId' | 'title' | 'kind' | 'customContent' | 'leftContent' | 'rightContent'>;

export function createLabSlide(p: SlidePayload): Promise<number> {
  return apiClient.post<ApiResponse<{ id: number }>>('/lab-decks/slides', { ...p, title: p.title.trim() }).then((r) => Number(r.data.data?.id) || 0);
}

export function updateLabSlide(id: number, p: SlidePayload): Promise<void> {
  return apiClient.put(`/lab-decks/slides/${id}`, { ...p, title: p.title.trim() }).then(() => undefined);
}

export function deleteLabSlide(id: number): Promise<void> {
  return apiClient.delete(`/lab-decks/slides/${id}`).then(() => undefined);
}

export function restoreLabSlide(id: number): Promise<void> {
  return apiClient.post(`/lab-decks/slides/${id}/restore`).then(() => undefined);
}

/** A deck's live slides in their new order; a slide from another deck moves into it. */
export function reorderLabSlides(deckId: number, ids: number[]): Promise<void> {
  return apiClient.post('/lab-decks/slides/reorder', { deckId, ids }).then(() => undefined);
}

export function listSlideTests(slideId: number): Promise<DeckTest[]> {
  return apiClient.get<ApiResponse<DeckTest[]>>(`/lab-decks/slides/${slideId}/tests`).then((r) => r.data.data ?? []);
}

function testBody(t: DeckTest) {
  const { id: _id, ...rest } = t;
  return { ...rest, title: t.title.trim(), labCode: t.labCode.trim() };
}

export function createSlideTest(slideId: number, t: DeckTest): Promise<number> {
  return apiClient.post<ApiResponse<{ id: number }>>(`/lab-decks/slides/${slideId}/tests`, testBody(t)).then((r) => Number(r.data.data?.id) || 0);
}

export function updateSlideTest(id: number, t: DeckTest): Promise<void> {
  return apiClient.put(`/lab-decks/tests/${id}`, testBody(t)).then(() => undefined);
}

/** Removed for good (the viewer would still show a test that was only switched off). */
export function deleteSlideTest(id: number): Promise<void> {
  return apiClient.delete(`/lab-decks/tests/${id}`).then(() => undefined);
}

export function listBiomarkers(): Promise<Biomarker[]> {
  return apiClient.get<ApiResponse<Biomarker[]>>('/lab-decks/biomarkers').then((r) => r.data.data ?? []);
}

export const ICON_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png';
export const ICON_MAX_BYTES = 4 * 1024 * 1024;

export function isAcceptedIcon(file: File): boolean {
  return /\.(jpe?g|png)$/i.test(file.name);
}

/** Uploads an icon into the account's icons folder; returns its file name. */
export async function uploadDeckIcon(file: File): Promise<string> {
  const r = await uploadFile('/lab-decks/icons', file, undefined, { fileFieldName: 'file' });
  const name = r?.data?.data?.fileName as string | undefined;
  if (!name) throw new Error('The upload finished but no file name came back.');
  return name;
}

/** An object URL for an icon (the caller revokes it), or null. */
export async function fetchDeckIconUrl(fileName: string): Promise<string | null> {
  const r = await apiClient.get<{ contentType?: string; data?: string }>('/lab-decks/icons', { params: { file: fileName } });
  const { contentType, data } = r.data ?? {};
  if (typeof data !== 'string' || typeof contentType !== 'string' || !contentType.startsWith('image/')) return null;
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  if (bytes.length === 0) return null;
  return URL.createObjectURL(new Blob([bytes], { type: contentType }));
}
